import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { loadDeckGl, type DeckApi, type DeckLayerCtor, type DeckViewCtor } from "@/lib/viz/cdnLoader";
import { prepareDeckGlSpec } from "@/lib/viz/dataSubstitution";
import type { VizRenderPayload } from "@/lib/viz/types";

interface Props {
  payload: VizRenderPayload;
  /**
   * Render a tabular fallback when the deck.gl bundle fails to load
   * or the spec errors. Without it, an offline / proxy-blocked CDN
   * would silently swallow the data; with it, the user at least sees
   * the rows. Defaults to {@code true} — the parent {@code VizRenderer}
   * relies on it.
   */
  fallbackTable?: boolean;
}

/**
 * deck.gl renderer. The standalone CDN bundle exposes a `deck` global
 * with the `Deck` class plus layer constructors at top level. We
 * instantiate `new deck.Deck({...})` on a dedicated container and
 * translate each layer config's `@@type` discriminator into the
 * corresponding constructor.
 *
 * <p>Operate-UI's v1 port: no basemap. Geographic layers render
 * against a transparent background — fine for BI density use cases,
 * less ideal for orientation. A basemap would pull in MapLibre or
 * Mapbox; design TBD.
 */
export const DeckGlRenderer: React.FC<Props> = ({ payload, fallbackTable = true }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let instance: { setProps: (c: Record<string, unknown>) => void; finalize: () => void } | null = null;

    (async () => {
      try {
        const api = await loadDeckGl();
        if (cancelled || !containerRef.current) return;
        const spec = prepareDeckGlSpec(payload);
        const layers = spec.layers.map((cfg) => instantiateLayer(api, cfg));
        const deckProps: Record<string, unknown> = {
          parent: containerRef.current,
          width:  "100%",
          height: "100%",
          initialViewState: spec.initialViewState,
          controller: spec.controller,
          layers,
          // A WebGL canvas only yields non-blank pixels from
          // toDataURL() — which the "Save as Cognos report" capture
          // relies on — when its drawing buffer is preserved. Costs a
          // little memory; harmless for BI-scale scenes.
          glOptions: { preserveDrawingBuffer: true },
        };
        // Non-geographic scenes (graphs, lineage) ship a `views` array
        // with @@type discriminators (e.g. "OrthographicView"). When
        // present we instantiate the matching deck.gl view ctors so the
        // scene renders flat-2D instead of through the default MapView
        // projection (which would interpret coordinates as lat/lon).
        if (spec.views && spec.views.length > 0) {
          deckProps.views = spec.views.map((cfg) => instantiateView(api, cfg));
        }
        instance = new api.Deck(deckProps);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "unknown error";
          setError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { instance?.finalize(); } catch { /* no-op */ }
    };
  }, [payload]);

  if (error) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          <div className="font-semibold">deck.gl scene could not render</div>
          <div className="mt-1 text-xs">{error}</div>
        </div>
        {fallbackTable && <TabularFallback payload={payload} />}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      // h-full so the scene fills its parent in the fullscreen viewer
      // (the parent gives it real height via flex-1); min-h-[360px]
      // keeps the inline chat-card render at the previous size when
      // no parent height is available.
      className="relative h-full min-h-[360px] w-full overflow-hidden rounded-md border border-slate-200 bg-slate-50"
    />
  );
};

/**
 * Translate a layer config object's `@@type` string into a real
 * constructor invocation. Throws when the type isn't exported by the
 * loaded deck.gl bundle — the error path falls back to the tabular
 * view above.
 */
function instantiateLayer(api: DeckApi, cfg: Record<string, unknown>): unknown {
  const typeKey = cfg["@@type"];
  if (typeof typeKey !== "string") {
    throw new Error("layer config missing @@type discriminator");
  }
  const ctor: DeckLayerCtor | undefined =
      (api as unknown as Record<string, DeckLayerCtor>)[typeKey];
  if (!ctor) {
    throw new Error(`deck.gl layer type '${typeKey}' is not exported by the loaded bundle`);
  }
  // deck.gl ignores unknown keys but the @@type sentinel is noise.
  const { ["@@type"]: _ignored, ...rest } = cfg;
  return new ctor(rest);
}

/**
 * Same pattern as {@link instantiateLayer}, applied to deck.gl view
 * classes (OrthographicView, MapView, OrbitView, …). The standalone
 * bundle exposes view constructors at the top level alongside layers,
 * so we look them up by name and strip the discriminator before
 * instantiating.
 */
function instantiateView(api: DeckApi, cfg: Record<string, unknown>): unknown {
  const typeKey = cfg["@@type"];
  if (typeof typeKey !== "string") {
    throw new Error("view config missing @@type discriminator");
  }
  const ctor: DeckViewCtor | undefined =
      (api as unknown as Record<string, DeckViewCtor>)[typeKey];
  if (!ctor) {
    throw new Error(`deck.gl view type '${typeKey}' is not exported by the loaded bundle`);
  }
  const { ["@@type"]: _ignored, ...rest } = cfg;
  return new ctor(rest);
}

/**
 * Inline-table view used when deck.gl can't render (CDN unreachable,
 * webgl unsupported, or a malformed layer spec). Mirrors the same
 * data the chart would have shown so the user isn't left with an
 * "8 rows are available below" promise that nothing fulfils.
 */
const TabularFallback: React.FC<{ payload: VizRenderPayload }> = ({ payload }) => {
  const { columnNames, rows } = payload;
  if (rows.length === 0) return null;
  const cap = 100;
  const visible = rows.slice(0, cap);
  return (
    <details className="rounded-md border border-slate-200 bg-white">
      <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-slate-700">
        View data ({rows.length} rows{rows.length > cap ? `, showing first ${cap}` : ""})
      </summary>
      <div className="max-h-96 overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-left">
            <tr>
              {columnNames.map((c) => (
                <th key={c} className="sticky top-0 border-b border-slate-200 bg-slate-50 px-2 py-1.5 font-semibold">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-b-0">
                {columnNames.map((c, j) => (
                  <td key={c} className="px-2 py-1 align-top">
                    {formatCell(row[j])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
};

function formatCell(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "number") return Number.isFinite(v) ? v.toLocaleString() : String(v);
  return String(v);
}
